import React, { useMemo, useState } from 'react';
import jsPDF from 'jspdf';
import {
  FaDownload,
  FaPrint,
  FaRedo,
  FaPlus,
  FaTrash,
  FaBroom,
  FaUtensils,
  FaCar,
  FaLeaf,
  FaBaby,
  FaUserNurse,
  FaUserShield,
  FaTshirt,
} from 'react-icons/fa';
import SEO from '../components/SEO';
import LogoUploader from '../components/LogoUploader';
import { drawPdfLogo } from '../utils/logoUpload';

const HELPER_ROLES = [
  { v: 'maid', l: 'House Maid', Icon: FaBroom },
  { v: 'cook', l: 'Cook', Icon: FaUtensils },
  { v: 'driver', l: 'Driver', Icon: FaCar },
  { v: 'gardener', l: 'Gardener', Icon: FaLeaf },
  { v: 'nanny', l: 'Nanny / Babysitter', Icon: FaBaby },
  { v: 'caretaker', l: 'Elder Caretaker', Icon: FaUserNurse },
  { v: 'guard', l: 'Security Guard', Icon: FaUserShield },
  { v: 'laundry', l: 'Laundry / Ironing', Icon: FaTshirt },
];

const TEMPLATES = [
  { id: 'classic', label: 'Classic A4', accent: '#0f766e', format: 'a4' },
  { id: 'modern', label: 'Modern Sidebar', accent: '#7c3aed', format: 'a4' },
  { id: 'minimal', label: 'Minimal Mono', accent: '#111827', format: 'a4' },
  { id: 'banner', label: 'Bold Banner', accent: '#b45309', format: 'a4' },
  { id: 'thermal', label: 'Quick Slip 80mm', accent: '#15803d', format: [80, 297] },
];

const PAYMENT_MODES = ['Cash', 'UPI', 'Bank Transfer', 'Cheque', 'Wallet'];

const hexToRgbArr = (hex) => {
  const h = hex.replace('#', '');
  return [
    parseInt(h.substring(0, 2), 16),
    parseInt(h.substring(2, 4), 16),
    parseInt(h.substring(4, 6), 16),
  ];
};

const pad2 = (n) => String(n).padStart(2, '0');
const toDMY = (d) => `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
const todayDMY = () => toDMY(new Date());
const nowHM = () => {
  const d = new Date();
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};
const monthYearLabel = () => {
  const d = new Date();
  return d.toLocaleString('en-IN', { month: 'long', year: 'numeric' });
};
const daysInCurrentMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
};
const genBillNo = () => {
  const d = new Date();
  const seq = Math.floor(1000 + Math.random() * 9000);
  return `DH-${d.getFullYear()}${pad2(d.getMonth() + 1)}-${seq}`;
};

const formatINR = (n) =>
  Number(n || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const newExtraTask = (description = '', date = todayDMY(), amount = 0) => ({
  id: Math.random().toString(36).slice(2, 9),
  description,
  date,
  amount,
});

const newAdvance = (date = todayDMY(), reason = '', amount = 0) => ({
  id: Math.random().toString(36).slice(2, 9),
  date,
  reason,
  amount,
});

const DailyHelperBillGenerator = () => {
  const [role, setRole] = useState('maid');
  const [template, setTemplate] = useState('classic');

  const [employer, setEmployer] = useState({
    name: 'Rakesh Verma',
    address: 'Flat 402, Lotus Heights, Wakad, Pune 411057',
    phone: '+91 98200 33445',
    email: 'rakesh.verma@gmail.com',
  });

  const [helper, setHelper] = useState({
    name: 'Sunita Devi',
    role: 'House Maid',
    idProof: 'Aadhaar XXXX-XXXX-4521',
    phone: '+91 91234 56789',
    address: 'H. No. 14, Kothrud, Pune',
    joinedOn: '01/01/2024',
    bankAccount: '',
    ifsc: '',
    upi: 'sunita@upi',
  });

  const [meta, setMeta] = useState({
    billNo: genBillNo(),
    period: monthYearLabel(),
    issueDate: todayDMY(),
    time: nowHM(),
    paymentMode: 'UPI',
    paidStatus: 'Paid',
  });

  const totalDays = daysInCurrentMonth();

  const [attendance, setAttendance] = useState({
    monthlySalary: 8000,
    workingDays: totalDays,
    daysPresent: totalDays - 2,
    paidLeaves: 1,
    unpaidLeaves: 1,
    overtimeHours: 0,
    overtimeRate: 50, // per hour
  });

  const [extras, setExtras] = useState([
    newExtraTask('Deep cleaning - kitchen', todayDMY(), 300),
  ]);

  const [allowances, setAllowances] = useState({
    bonus: 0,
    festivalBonus: 0,
    travelAllowance: 0,
    foodAllowance: 0,
    other: 0,
  });

  const [advances, setAdvances] = useState([
    newAdvance(todayDMY(), 'Emergency advance', 500),
  ]);

  const [deductions, setDeductions] = useState({
    breakage: 0,
    other: 0,
    roundOff: true,
  });

  const [notes, setNotes] = useState(
    'Thank you for your dedicated service. Salary credited as per agreed terms.'
  );

  const [logo, setLogo] = useState(null);

  const accent = TEMPLATES.find((t) => t.id === template)?.accent || '#0f766e';
  const roleConfig = HELPER_ROLES.find((r) => r.v === role);

  // ---- Switch role → swap defaults ----
  const switchRole = (r) => {
    setRole(r);
    const rc = HELPER_ROLES.find((x) => x.v === r);
    if (!rc) return;
    setHelper((h) => ({ ...h, role: rc.l }));
    const defaults = {
      maid: { monthlySalary: 8000, name: 'Sunita Devi' },
      cook: { monthlySalary: 10000, name: 'Lakshmi Iyer' },
      driver: { monthlySalary: 18000, name: 'Mahesh Kumar' },
      gardener: { monthlySalary: 5000, name: 'Ramesh Yadav' },
      nanny: { monthlySalary: 15000, name: 'Anita Roy' },
      caretaker: { monthlySalary: 16000, name: 'Geeta Pawar' },
      guard: { monthlySalary: 12000, name: 'Vikram Singh' },
      laundry: { monthlySalary: 3500, name: 'Shanti Bai' },
    }[r];
    if (defaults) {
      setHelper((h) => ({ ...h, name: defaults.name, role: rc.l }));
      setAttendance((a) => ({ ...a, monthlySalary: defaults.monthlySalary }));
    }
  };

  // ---------- COMPUTATIONS ----------
  const totals = useMemo(() => {
    const monthly = Number(attendance.monthlySalary) || 0;
    const workDays = Math.max(1, Number(attendance.workingDays) || totalDays);
    const present = Number(attendance.daysPresent) || 0;
    const paidL = Number(attendance.paidLeaves) || 0;
    const unpaidL = Number(attendance.unpaidLeaves) || 0;
    const perDay = monthly / workDays;
    const payableDays = present + paidL;
    const earnedBase = perDay * payableDays;
    const lopDays = Math.max(0, workDays - present - paidL - unpaidL);
    const lopAmount = perDay * (unpaidL + lopDays);

    const ot = (Number(attendance.overtimeHours) || 0) * (Number(attendance.overtimeRate) || 0);

    const extrasTotal = extras.reduce((s, e) => s + (Number(e.amount) || 0), 0);

    const allowanceTotal =
      (Number(allowances.bonus) || 0) +
      (Number(allowances.festivalBonus) || 0) +
      (Number(allowances.travelAllowance) || 0) +
      (Number(allowances.foodAllowance) || 0) +
      (Number(allowances.other) || 0);

    const advanceTotal = advances.reduce((s, a) => s + (Number(a.amount) || 0), 0);

    const deductionTotal =
      (Number(deductions.breakage) || 0) + (Number(deductions.other) || 0);

    const gross = earnedBase + ot + extrasTotal + allowanceTotal;
    const netRaw = Math.max(0, gross - advanceTotal - deductionTotal);
    const netRounded = Math.round(netRaw);

    return {
      perDay,
      payableDays,
      earnedBase,
      lopDays,
      lopAmount,
      ot,
      extrasTotal,
      allowanceTotal,
      advanceTotal,
      deductionTotal,
      gross,
      net: Math.round(netRaw * 100) / 100,
      netRounded,
      roundOff: netRounded - netRaw,
    };
  }, [attendance, extras, allowances, advances, deductions, totalDays]);

  const finalAmount = deductions.roundOff ? totals.netRounded : totals.net;

  // ---------- HANDLERS ----------
  const updateEmployer = (k, v) => setEmployer((p) => ({ ...p, [k]: v }));
  const updateHelper = (k, v) => setHelper((p) => ({ ...p, [k]: v }));
  const updateMeta = (k, v) => setMeta((p) => ({ ...p, [k]: v }));
  const updateAttendance = (k, v) =>
    setAttendance((p) => ({ ...p, [k]: v === '' ? '' : (isNaN(Number(v)) ? v : Number(v)) }));
  const updateAllowances = (k, v) =>
    setAllowances((p) => ({ ...p, [k]: v === '' ? 0 : Number(v) || 0 }));
  const updateDeductions = (k, v) =>
    setDeductions((p) => ({ ...p, [k]: typeof v === 'boolean' ? v : (v === '' ? 0 : Number(v) || 0) }));

  const addExtra = () => setExtras((p) => [...p, newExtraTask()]);
  const removeExtra = (id) => setExtras((p) => p.filter((e) => e.id !== id));
  const updateExtra = (id, k, v) =>
    setExtras((p) => p.map((e) => (e.id === id ? { ...e, [k]: k === 'amount' ? (v === '' ? 0 : Number(v) || 0) : v } : e)));

  const addAdvance = () => setAdvances((p) => [...p, newAdvance()]);
  const removeAdvance = (id) => setAdvances((p) => p.filter((a) => a.id !== id));
  const updateAdvance = (id, k, v) =>
    setAdvances((p) => p.map((a) => (a.id === id ? { ...a, [k]: k === 'amount' ? (v === '' ? 0 : Number(v) || 0) : v } : a)));

  const handleReset = () => {
    setRole('maid');
    setTemplate('classic');
    setEmployer({
      name: 'Rakesh Verma',
      address: 'Flat 402, Lotus Heights, Wakad, Pune 411057',
      phone: '+91 98200 33445',
      email: 'rakesh.verma@gmail.com',
    });
    setHelper({
      name: 'Sunita Devi',
      role: 'House Maid',
      idProof: 'Aadhaar XXXX-XXXX-4521',
      phone: '+91 91234 56789',
      address: 'H. No. 14, Kothrud, Pune',
      joinedOn: '01/01/2024',
      bankAccount: '',
      ifsc: '',
      upi: 'sunita@upi',
    });
    setMeta({
      billNo: genBillNo(),
      period: monthYearLabel(),
      issueDate: todayDMY(),
      time: nowHM(),
      paymentMode: 'UPI',
      paidStatus: 'Paid',
    });
    setAttendance({
      monthlySalary: 8000,
      workingDays: totalDays,
      daysPresent: totalDays - 2,
      paidLeaves: 1,
      unpaidLeaves: 1,
      overtimeHours: 0,
      overtimeRate: 50,
    });
    setExtras([newExtraTask('Deep cleaning - kitchen', todayDMY(), 300)]);
    setAllowances({ bonus: 0, festivalBonus: 0, travelAllowance: 0, foodAllowance: 0, other: 0 });
    setAdvances([newAdvance(todayDMY(), 'Emergency advance', 500)]);
    setDeductions({ breakage: 0, other: 0, roundOff: true });
    setNotes('Thank you for your dedicated service. Salary credited as per agreed terms.');
    setLogo(null);
  };

  const handlePrint = () => window.print();

  // ---------- PDF RENDERERS ----------
  const buildDoc = () => {
    const tpl = TEMPLATES.find((t) => t.id === template);
    const format = tpl?.format || 'a4';
    return new jsPDF({ orientation: 'portrait', unit: 'mm', format });
  };

  const renderClassicPDF = (doc) => {
    const accentRGB = hexToRgbArr(accent);
    const pageW = doc.internal.pageSize.getWidth();
    let y = 14;

    if (logo) drawPdfLogo(doc, logo, 14, y, 22, 22);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(...accentRGB);
    doc.text(employer.name || 'Household', logo ? 40 : 14, y + 8);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(60);
    doc.text(employer.address || '', logo ? 40 : 14, y + 14);
    doc.text(`Phone: ${employer.phone || ''}   Email: ${employer.email || ''}`, logo ? 40 : 14, y + 19);

    y += 30;
    doc.setDrawColor(...accentRGB);
    doc.setLineWidth(0.6);
    doc.line(14, y, pageW - 14, y);

    y += 8;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(...accentRGB);
    doc.text('DAILY HELPER SALARY BILL', pageW / 2, y, { align: 'center' });

    y += 7;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(60);
    doc.text(`Bill No: ${meta.billNo}`, 14, y);
    doc.text(`Pay Period: ${meta.period}`, pageW / 2, y, { align: 'center' });
    doc.text(`Date: ${meta.issueDate}`, pageW - 14, y, { align: 'right' });

    // Helper details box
    y += 8;
    doc.setDrawColor(220);
    doc.setLineWidth(0.3);
    doc.rect(14, y, pageW - 28, 30);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(40);
    doc.text('HELPER DETAILS', 17, y + 5);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(`Name: ${helper.name}`, 17, y + 11);
    doc.text(`Role: ${helper.role}`, 17, y + 16);
    doc.text(`Phone: ${helper.phone}`, 17, y + 21);
    doc.text(`ID Proof: ${helper.idProof}`, 17, y + 26);
    doc.text(`Joined: ${helper.joinedOn}`, pageW / 2 + 5, y + 11);
    doc.text(`UPI: ${helper.upi || '-'}`, pageW / 2 + 5, y + 16);
    doc.text(`A/C: ${helper.bankAccount || '-'}`, pageW / 2 + 5, y + 21);
    doc.text(`IFSC: ${helper.ifsc || '-'}`, pageW / 2 + 5, y + 26);

    y += 36;
    // Attendance table
    doc.setFillColor(...accentRGB);
    doc.setTextColor(255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.rect(14, y, pageW - 28, 7, 'F');
    doc.text('ATTENDANCE & EARNINGS', 17, y + 5);
    y += 10;

    doc.setTextColor(40);
    doc.setFont('helvetica', 'normal');
    const rows = [
      ['Monthly Salary', formatINR(attendance.monthlySalary)],
      ['Working Days in Month', String(attendance.workingDays)],
      ['Days Present', String(attendance.daysPresent)],
      ['Paid Leaves', String(attendance.paidLeaves)],
      ['Unpaid Leaves / LOP Days', `${attendance.unpaidLeaves} (LOP: ${totals.lopDays})`],
      ['Per Day Wage', formatINR(totals.perDay)],
      ['Payable Days', String(totals.payableDays)],
      ['Earned Base Salary', formatINR(totals.earnedBase)],
      ['Overtime (hrs x rate)', `${attendance.overtimeHours} x ${formatINR(attendance.overtimeRate)} = ${formatINR(totals.ot)}`],
    ];
    rows.forEach((r, i) => {
      if (i % 2 === 0) {
        doc.setFillColor(248, 250, 252);
        doc.rect(14, y - 3, pageW - 28, 6, 'F');
      }
      doc.text(r[0], 17, y + 1);
      doc.text(r[1], pageW - 17, y + 1, { align: 'right' });
      y += 6;
    });

    // Extras
    if (extras.length) {
      y += 4;
      doc.setFillColor(...accentRGB);
      doc.setTextColor(255);
      doc.setFont('helvetica', 'bold');
      doc.rect(14, y, pageW - 28, 7, 'F');
      doc.text('EXTRA TASKS / ONE-TIME WORK', 17, y + 5);
      y += 10;
      doc.setTextColor(40);
      doc.setFont('helvetica', 'normal');
      extras.forEach((e, i) => {
        if (i % 2 === 0) {
          doc.setFillColor(248, 250, 252);
          doc.rect(14, y - 3, pageW - 28, 6, 'F');
        }
        doc.text(`${e.date}  ${e.description}`, 17, y + 1);
        doc.text(formatINR(e.amount), pageW - 17, y + 1, { align: 'right' });
        y += 6;
      });
    }

    // Allowances
    const allowanceItems = [
      ['Monthly Bonus', allowances.bonus],
      ['Festival Bonus', allowances.festivalBonus],
      ['Travel Allowance', allowances.travelAllowance],
      ['Food / Tiffin Allowance', allowances.foodAllowance],
      ['Other Allowance', allowances.other],
    ].filter((r) => Number(r[1]) > 0);
    if (allowanceItems.length) {
      y += 4;
      doc.setFillColor(...accentRGB);
      doc.setTextColor(255);
      doc.setFont('helvetica', 'bold');
      doc.rect(14, y, pageW - 28, 7, 'F');
      doc.text('ALLOWANCES & BONUS', 17, y + 5);
      y += 10;
      doc.setTextColor(40);
      doc.setFont('helvetica', 'normal');
      allowanceItems.forEach((r, i) => {
        if (i % 2 === 0) {
          doc.setFillColor(248, 250, 252);
          doc.rect(14, y - 3, pageW - 28, 6, 'F');
        }
        doc.text(r[0], 17, y + 1);
        doc.text(formatINR(r[1]), pageW - 17, y + 1, { align: 'right' });
        y += 6;
      });
    }

    // Advances + deductions
    const dedItems = [
      ...advances.map((a) => [`Advance: ${a.date} - ${a.reason || '-'}`, a.amount]),
      ...(Number(deductions.breakage) > 0 ? [['Breakage / Damage', deductions.breakage]] : []),
      ...(Number(deductions.other) > 0 ? [['Other Deduction', deductions.other]] : []),
    ];
    if (dedItems.length) {
      y += 4;
      doc.setFillColor(185, 28, 28);
      doc.setTextColor(255);
      doc.setFont('helvetica', 'bold');
      doc.rect(14, y, pageW - 28, 7, 'F');
      doc.text('ADVANCES & DEDUCTIONS', 17, y + 5);
      y += 10;
      doc.setTextColor(40);
      doc.setFont('helvetica', 'normal');
      dedItems.forEach((r, i) => {
        if (i % 2 === 0) {
          doc.setFillColor(254, 242, 242);
          doc.rect(14, y - 3, pageW - 28, 6, 'F');
        }
        doc.text(String(r[0]), 17, y + 1);
        doc.text(`- ${formatINR(r[1])}`, pageW - 17, y + 1, { align: 'right' });
        y += 6;
      });
    }

    // Summary
    y += 6;
    doc.setDrawColor(...accentRGB);
    doc.setLineWidth(0.4);
    doc.line(pageW / 2, y, pageW - 14, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(60);
    const sumRow = (label, val) => {
      doc.text(label, pageW / 2 + 4, y);
      doc.text(formatINR(val), pageW - 17, y, { align: 'right' });
      y += 5;
    };
    sumRow('Gross Payable', totals.gross);
    sumRow('Total Advances', totals.advanceTotal);
    sumRow('Total Deductions', totals.deductionTotal);
    if (deductions.roundOff) sumRow('Round Off', totals.roundOff);

    y += 2;
    doc.setFillColor(...accentRGB);
    doc.rect(pageW / 2, y, pageW / 2 - 14, 9, 'F');
    doc.setTextColor(255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('NET PAYABLE', pageW / 2 + 4, y + 6);
    doc.text(`Rs. ${formatINR(finalAmount)}`, pageW - 17, y + 6, { align: 'right' });
    y += 14;

    doc.setTextColor(40);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(`Payment Mode: ${meta.paymentMode}`, 14, y);
    doc.text(`Status: ${meta.paidStatus}`, pageW - 14, y, { align: 'right' });

    if (notes) {
      y += 8;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.text('Notes:', 14, y);
      doc.setFont('helvetica', 'normal');
      const noteLines = doc.splitTextToSize(notes, pageW - 28);
      doc.text(noteLines, 14, y + 5);
      y += 5 + noteLines.length * 4;
    }

    // Signatures
    y = Math.max(y, doc.internal.pageSize.getHeight() - 30);
    doc.setDrawColor(120);
    doc.setLineWidth(0.3);
    doc.line(20, y, 80, y);
    doc.line(pageW - 80, y, pageW - 20, y);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(80);
    doc.text('Helper Signature / Thumb', 50, y + 4, { align: 'center' });
    doc.text('Employer Signature', pageW - 50, y + 4, { align: 'center' });
  };

  const renderModernPDF = (doc) => {
    const accentRGB = hexToRgbArr(accent);
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();

    // Sidebar
    doc.setFillColor(...accentRGB);
    doc.rect(0, 0, 55, pageH, 'F');
    doc.setTextColor(255);
    if (logo) drawPdfLogo(doc, logo, 8, 10, 22, 22);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text(employer.name || 'Household', 8, logo ? 40 : 18);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    const empAddr = doc.splitTextToSize(employer.address || '', 44);
    doc.text(empAddr, 8, logo ? 46 : 24);
    doc.text(employer.phone || '', 8, logo ? 60 : 38);
    doc.text(employer.email || '', 8, logo ? 64 : 42);

    doc.setFontSize(8);
    doc.text('BILL NO', 8, 90);
    doc.setFont('helvetica', 'bold');
    doc.text(meta.billNo, 8, 95);
    doc.setFont('helvetica', 'normal');
    doc.text('PERIOD', 8, 105);
    doc.setFont('helvetica', 'bold');
    doc.text(meta.period, 8, 110);
    doc.setFont('helvetica', 'normal');
    doc.text('DATE', 8, 120);
    doc.setFont('helvetica', 'bold');
    doc.text(meta.issueDate, 8, 125);
    doc.setFont('helvetica', 'normal');
    doc.text('PAYMENT', 8, 135);
    doc.setFont('helvetica', 'bold');
    doc.text(meta.paymentMode, 8, 140);

    // Main
    let y = 18;
    doc.setTextColor(...accentRGB);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.text('SALARY BILL', 62, y);
    doc.setFontSize(11);
    doc.setTextColor(80);
    doc.text(helper.role, 62, y + 7);

    y += 18;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...accentRGB);
    doc.text('PAID TO', 62, y);
    doc.setTextColor(40);
    doc.setFontSize(10);
    doc.text(helper.name, 62, y + 5);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(`Phone: ${helper.phone}`, 62, y + 10);
    doc.text(`ID: ${helper.idProof}`, 62, y + 14);
    doc.text(`UPI: ${helper.upi || '-'}`, 62, y + 18);

    y += 28;
    // Earnings & deductions side-by-side
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...accentRGB);
    doc.text('EARNINGS', 62, y);
    doc.text('DEDUCTIONS', 140, y);
    doc.setDrawColor(...accentRGB);
    doc.line(62, y + 2, 130, y + 2);
    doc.line(140, y + 2, pageW - 14, y + 2);

    y += 7;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(40);
    const earnings = [
      ['Earned Base', totals.earnedBase],
      ['Overtime', totals.ot],
      ['Extra Tasks', totals.extrasTotal],
      ['Allowances', totals.allowanceTotal],
    ];
    let ey = y;
    earnings.forEach((r) => {
      doc.text(r[0], 62, ey);
      doc.text(formatINR(r[1]), 130, ey, { align: 'right' });
      ey += 5;
    });

    const ded = [
      ['Advances', totals.advanceTotal],
      ['Breakage', deductions.breakage],
      ['Other', deductions.other],
    ];
    let dy = y;
    ded.forEach((r) => {
      doc.text(r[0], 140, dy);
      doc.text(formatINR(r[1]), pageW - 14, dy, { align: 'right' });
      dy += 5;
    });

    y = Math.max(ey, dy) + 4;
    doc.setDrawColor(200);
    doc.line(62, y, pageW - 14, y);
    y += 6;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text(`Gross: Rs. ${formatINR(totals.gross)}`, 62, y);
    doc.text(`Deductions: Rs. ${formatINR(totals.advanceTotal + totals.deductionTotal)}`, pageW - 14, y, { align: 'right' });
    y += 10;

    doc.setFillColor(...accentRGB);
    doc.rect(62, y, pageW - 76, 14, 'F');
    doc.setTextColor(255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('NET PAYABLE', 66, y + 9);
    doc.text(`Rs. ${formatINR(finalAmount)}`, pageW - 18, y + 9, { align: 'right' });

    if (notes) {
      y += 22;
      doc.setTextColor(80);
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(8);
      const nl = doc.splitTextToSize(notes, pageW - 76);
      doc.text(nl, 62, y);
    }
  };

  const renderMinimalPDF = (doc) => {
    const pageW = doc.internal.pageSize.getWidth();
    let y = 18;
    doc.setTextColor(20);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.text('Salary Bill', 14, y);
    if (logo) drawPdfLogo(doc, logo, pageW - 36, y - 8, 22, 22);

    y += 6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(110);
    doc.text(`${meta.billNo} · ${meta.period} · ${meta.issueDate}`, 14, y);

    y += 12;
    doc.setDrawColor(20);
    doc.setLineWidth(0.4);
    doc.line(14, y, pageW - 14, y);

    y += 8;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(20);
    doc.text('From', 14, y);
    doc.text('To', pageW / 2 + 4, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(60);
    doc.text(employer.name, 14, y);
    doc.text(helper.name, pageW / 2 + 4, y);
    y += 4;
    const fromAddr = doc.splitTextToSize(employer.address || '', pageW / 2 - 18);
    doc.text(fromAddr, 14, y);
    doc.text(helper.role, pageW / 2 + 4, y);
    y += 4;
    doc.text(helper.phone, pageW / 2 + 4, y);

    y = Math.max(y, 70);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(20);
    doc.text('Breakdown', 14, y);
    y += 6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(40);

    const rows = [
      ['Earned base salary', formatINR(totals.earnedBase)],
      ['Overtime', formatINR(totals.ot)],
      ['Extra tasks', formatINR(totals.extrasTotal)],
      ['Allowances & bonus', formatINR(totals.allowanceTotal)],
      ['— Advances', `- ${formatINR(totals.advanceTotal)}`],
      ['— Deductions', `- ${formatINR(totals.deductionTotal)}`],
    ];
    rows.forEach((r) => {
      doc.text(r[0], 14, y);
      doc.text(r[1], pageW - 14, y, { align: 'right' });
      y += 5;
    });

    y += 4;
    doc.setDrawColor(20);
    doc.line(14, y, pageW - 14, y);
    y += 7;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text('Net Payable', 14, y);
    doc.text(`Rs. ${formatINR(finalAmount)}`, pageW - 14, y, { align: 'right' });

    y += 8;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(110);
    doc.text(`Paid via ${meta.paymentMode} · ${meta.paidStatus}`, 14, y);

    if (notes) {
      y += 8;
      const nl = doc.splitTextToSize(notes, pageW - 28);
      doc.text(nl, 14, y);
    }
  };

  const renderBannerPDF = (doc) => {
    const accentRGB = hexToRgbArr(accent);
    const pageW = doc.internal.pageSize.getWidth();
    doc.setFillColor(...accentRGB);
    doc.rect(0, 0, pageW, 42, 'F');
    doc.setTextColor(255);
    if (logo) drawPdfLogo(doc, logo, 14, 10, 22, 22);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.text('DAILY HELPER BILL', logo ? 42 : 14, 22);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(employer.name || '', logo ? 42 : 14, 30);
    doc.setFontSize(8);
    doc.text(`${meta.billNo}  ·  ${meta.period}`, pageW - 14, 18, { align: 'right' });
    doc.text(`Date: ${meta.issueDate}`, pageW - 14, 24, { align: 'right' });
    doc.text(`Mode: ${meta.paymentMode}`, pageW - 14, 30, { align: 'right' });

    let y = 52;
    doc.setTextColor(40);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(`${helper.name} — ${helper.role}`, 14, y);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    y += 6;
    doc.text(`Phone: ${helper.phone}   ID: ${helper.idProof}`, 14, y);
    y += 5;
    doc.text(`Joined: ${helper.joinedOn}   UPI: ${helper.upi || '-'}`, 14, y);

    y += 10;
    doc.setFillColor(255, 247, 237);
    doc.rect(14, y, pageW - 28, 8, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...accentRGB);
    doc.text('ATTENDANCE', 17, y + 5.5);
    y += 12;
    doc.setTextColor(40);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    const attRows = [
      [`Monthly Salary`, formatINR(attendance.monthlySalary)],
      [`Working Days`, String(attendance.workingDays)],
      [`Days Present`, String(attendance.daysPresent)],
      [`Paid Leaves`, String(attendance.paidLeaves)],
      [`Unpaid + LOP`, String(Number(attendance.unpaidLeaves) + totals.lopDays)],
      [`Per-day Wage`, formatINR(totals.perDay)],
    ];
    attRows.forEach((r) => {
      doc.text(r[0], 17, y);
      doc.text(r[1], pageW - 17, y, { align: 'right' });
      y += 5;
    });

    y += 4;
    doc.setFillColor(255, 247, 237);
    doc.rect(14, y, pageW - 28, 8, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...accentRGB);
    doc.text('AMOUNT BREAKDOWN', 17, y + 5.5);
    y += 12;
    doc.setTextColor(40);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    const breakRows = [
      ['Earned Base', formatINR(totals.earnedBase)],
      ['Overtime', formatINR(totals.ot)],
      ['Extra Tasks', formatINR(totals.extrasTotal)],
      ['Allowances', formatINR(totals.allowanceTotal)],
      ['Gross', formatINR(totals.gross)],
      ['Less: Advances', `- ${formatINR(totals.advanceTotal)}`],
      ['Less: Deductions', `- ${formatINR(totals.deductionTotal)}`],
    ];
    breakRows.forEach((r) => {
      doc.text(r[0], 17, y);
      doc.text(r[1], pageW - 17, y, { align: 'right' });
      y += 5;
    });

    y += 4;
    doc.setFillColor(...accentRGB);
    doc.rect(14, y, pageW - 28, 14, 'F');
    doc.setTextColor(255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text('NET PAYABLE', 17, y + 9);
    doc.text(`Rs. ${formatINR(finalAmount)}`, pageW - 17, y + 9, { align: 'right' });

    if (notes) {
      y += 22;
      doc.setTextColor(80);
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(8);
      const nl = doc.splitTextToSize(notes, pageW - 28);
      doc.text(nl, 14, y);
    }
  };

  const renderThermalPDF = (doc) => {
    const accentRGB = hexToRgbArr(accent);
    const pageW = doc.internal.pageSize.getWidth();
    let y = 6;
    if (logo) {
      drawPdfLogo(doc, logo, pageW / 2 - 7, y, 14, 14);
      y += 16;
    }
    doc.setTextColor(...accentRGB);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text(employer.name || '', pageW / 2, y, { align: 'center' });
    y += 4;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(60);
    const aw = doc.splitTextToSize(employer.address || '', pageW - 6);
    doc.text(aw, pageW / 2, y, { align: 'center' });
    y += aw.length * 3 + 1;
    doc.text(employer.phone || '', pageW / 2, y, { align: 'center' });

    y += 4;
    doc.setDrawColor(...accentRGB);
    doc.setLineDashPattern([1, 1], 0);
    doc.line(3, y, pageW - 3, y);
    doc.setLineDashPattern([], 0);

    y += 4;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...accentRGB);
    doc.text('HELPER SALARY SLIP', pageW / 2, y, { align: 'center' });
    y += 4;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(40);
    doc.text(`No: ${meta.billNo}`, 3, y);
    y += 3;
    doc.text(`Period: ${meta.period}`, 3, y);
    y += 3;
    doc.text(`Date: ${meta.issueDate}  ${meta.time}`, 3, y);
    y += 3;
    doc.text(`To: ${helper.name} (${helper.role})`, 3, y);
    y += 3;
    doc.text(`Phone: ${helper.phone}`, 3, y);

    y += 4;
    doc.setLineDashPattern([1, 1], 0);
    doc.line(3, y, pageW - 3, y);
    doc.setLineDashPattern([], 0);

    y += 4;
    doc.setFont('helvetica', 'bold');
    doc.text('ITEM', 3, y);
    doc.text('AMT', pageW - 3, y, { align: 'right' });
    y += 3;
    doc.setFont('helvetica', 'normal');
    const tlines = [
      ['Base', totals.earnedBase],
      ['Overtime', totals.ot],
      ['Extras', totals.extrasTotal],
      ['Allowance', totals.allowanceTotal],
      ['- Advance', -totals.advanceTotal],
      ['- Deduct', -totals.deductionTotal],
    ];
    tlines.forEach((r) => {
      doc.text(r[0], 3, y);
      doc.text(formatINR(r[1]), pageW - 3, y, { align: 'right' });
      y += 3;
    });

    y += 1;
    doc.setLineDashPattern([1, 1], 0);
    doc.line(3, y, pageW - 3, y);
    doc.setLineDashPattern([], 0);

    y += 4;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...accentRGB);
    doc.text('NET', 3, y);
    doc.text(`Rs.${formatINR(finalAmount)}`, pageW - 3, y, { align: 'right' });

    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(60);
    doc.text(`Mode: ${meta.paymentMode}  ·  ${meta.paidStatus}`, pageW / 2, y, { align: 'center' });
    y += 4;
    doc.text('Thank you!', pageW / 2, y, { align: 'center' });
  };

  const TEMPLATE_RENDERERS = {
    classic: renderClassicPDF,
    modern: renderModernPDF,
    minimal: renderMinimalPDF,
    banner: renderBannerPDF,
    thermal: renderThermalPDF,
  };

  const handleDownloadPDF = () => {
    const doc = buildDoc();
    (TEMPLATE_RENDERERS[template] || renderClassicPDF)(doc);
    const name = (helper.name || 'helper').replace(/\s+/g, '_');
    doc.save(`${name}_${meta.period.replace(/\s+/g, '_')}_bill.pdf`);
  };

  // ---------- PREVIEW ----------
  const Preview = () => {
    if (template === 'thermal') {
      return (
        <div style={{ background: '#fff', color: '#111', padding: 14, borderRadius: 8, maxWidth: 280, margin: '0 auto', fontFamily: 'monospace', fontSize: 12, border: `2px dashed ${accent}` }}>
          <div style={{ textAlign: 'center', color: accent, fontWeight: 700 }}>{employer.name}</div>
          <div style={{ textAlign: 'center', fontSize: 10, color: '#555' }}>{employer.address}</div>
          <div style={{ textAlign: 'center', fontSize: 10 }}>{employer.phone}</div>
          <hr style={{ borderTop: `1px dashed ${accent}` }} />
          <div style={{ textAlign: 'center', fontWeight: 700, color: accent }}>HELPER SALARY SLIP</div>
          <div style={{ fontSize: 10 }}>
            <div>No: {meta.billNo}</div>
            <div>Period: {meta.period}</div>
            <div>Date: {meta.issueDate} {meta.time}</div>
            <div>To: {helper.name} ({helper.role})</div>
            <div>Phone: {helper.phone}</div>
          </div>
          <hr style={{ borderTop: `1px dashed ${accent}` }} />
          <div style={{ display: 'flex', justifyContent: 'space-between' }}><b>Base</b><b>{formatINR(totals.earnedBase)}</b></div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Overtime</span><span>{formatINR(totals.ot)}</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Extras</span><span>{formatINR(totals.extrasTotal)}</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Allowance</span><span>{formatINR(totals.allowanceTotal)}</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>- Advance</span><span>{formatINR(totals.advanceTotal)}</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>- Deduct</span><span>{formatINR(totals.deductionTotal)}</span></div>
          <hr style={{ borderTop: `1px dashed ${accent}` }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', color: accent, fontWeight: 700 }}><span>NET</span><span>Rs.{formatINR(finalAmount)}</span></div>
          <div style={{ textAlign: 'center', fontSize: 10, marginTop: 6 }}>Mode: {meta.paymentMode} · {meta.paidStatus}</div>
          <div style={{ textAlign: 'center', fontSize: 10 }}>Thank you!</div>
        </div>
      );
    }

    const isModern = template === 'modern';
    const isMinimal = template === 'minimal';
    const isBanner = template === 'banner';

    return (
      <div style={{
        background: '#fff', color: '#111', padding: isModern ? 0 : 24, borderRadius: 10,
        maxWidth: 800, margin: '0 auto',
        border: `1px solid ${isMinimal ? '#222' : 'rgba(0,0,0,0.08)'}`,
        display: isModern ? 'flex' : 'block', overflow: 'hidden',
      }}>
        {isModern && (
          <div style={{ background: accent, color: '#fff', padding: 18, width: 180 }}>
            {logo && <img src={logo} alt="logo" style={{ width: 50, height: 50, marginBottom: 8 }} />}
            <div style={{ fontWeight: 700, fontSize: 14 }}>{employer.name}</div>
            <div style={{ fontSize: 11, marginTop: 4, opacity: 0.9 }}>{employer.address}</div>
            <div style={{ fontSize: 11, marginTop: 4 }}>{employer.phone}</div>
            <div style={{ fontSize: 11 }}>{employer.email}</div>
            <div style={{ marginTop: 18, fontSize: 10, opacity: 0.8 }}>BILL NO</div>
            <div style={{ fontWeight: 700, fontSize: 12 }}>{meta.billNo}</div>
            <div style={{ marginTop: 8, fontSize: 10, opacity: 0.8 }}>PERIOD</div>
            <div style={{ fontWeight: 700, fontSize: 12 }}>{meta.period}</div>
            <div style={{ marginTop: 8, fontSize: 10, opacity: 0.8 }}>DATE</div>
            <div style={{ fontWeight: 700, fontSize: 12 }}>{meta.issueDate}</div>
            <div style={{ marginTop: 8, fontSize: 10, opacity: 0.8 }}>PAYMENT</div>
            <div style={{ fontWeight: 700, fontSize: 12 }}>{meta.paymentMode}</div>
          </div>
        )}
        <div style={{ flex: 1, padding: isModern ? 24 : 0 }}>
          {isBanner && (
            <div style={{ background: accent, color: '#fff', margin: -24, marginBottom: 16, padding: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {logo && <img src={logo} alt="" style={{ width: 50, height: 50 }} />}
                <div>
                  <div style={{ fontSize: 20, fontWeight: 800 }}>DAILY HELPER BILL</div>
                  <div style={{ fontSize: 12, opacity: 0.9 }}>{employer.name}</div>
                </div>
              </div>
              <div style={{ textAlign: 'right', fontSize: 11 }}>
                <div>{meta.billNo}</div>
                <div>{meta.period}</div>
                <div>{meta.issueDate}</div>
              </div>
            </div>
          )}

          {!isModern && !isBanner && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
              <div style={{ display: 'flex', gap: 12 }}>
                {logo && <img src={logo} alt="" style={{ width: 56, height: 56 }} />}
                <div>
                  <div style={{ fontWeight: 800, fontSize: 18, color: isMinimal ? '#111' : accent }}>{employer.name}</div>
                  <div style={{ fontSize: 12, color: '#444' }}>{employer.address}</div>
                  <div style={{ fontSize: 12, color: '#666' }}>{employer.phone} · {employer.email}</div>
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontWeight: 700, color: isMinimal ? '#111' : accent }}>SALARY BILL</div>
                <div style={{ fontSize: 12 }}>{meta.billNo}</div>
                <div style={{ fontSize: 12 }}>{meta.period}</div>
                <div style={{ fontSize: 12 }}>{meta.issueDate}</div>
              </div>
            </div>
          )}

          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: 12, marginBottom: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, fontSize: 13 }}>
              <div><b>Helper:</b> {helper.name}</div>
              <div><b>Role:</b> {helper.role}</div>
              <div><b>Phone:</b> {helper.phone}</div>
              <div><b>ID:</b> {helper.idProof}</div>
              <div><b>Joined:</b> {helper.joinedOn}</div>
              <div><b>UPI:</b> {helper.upi || '-'}</div>
            </div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <div style={{ fontWeight: 700, color: isMinimal ? '#111' : accent, marginBottom: 6 }}>Attendance & Earnings</div>
            <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
              <tbody>
                <tr><td style={{ padding: '4px 0' }}>Monthly Salary</td><td style={{ textAlign: 'right' }}>₹ {formatINR(attendance.monthlySalary)}</td></tr>
                <tr><td>Working Days</td><td style={{ textAlign: 'right' }}>{attendance.workingDays}</td></tr>
                <tr><td>Days Present</td><td style={{ textAlign: 'right' }}>{attendance.daysPresent}</td></tr>
                <tr><td>Paid Leaves</td><td style={{ textAlign: 'right' }}>{attendance.paidLeaves}</td></tr>
                <tr><td>Unpaid + LOP</td><td style={{ textAlign: 'right' }}>{Number(attendance.unpaidLeaves) + totals.lopDays}</td></tr>
                <tr><td>Per Day Wage</td><td style={{ textAlign: 'right' }}>₹ {formatINR(totals.perDay)}</td></tr>
                <tr><td>Payable Days</td><td style={{ textAlign: 'right' }}>{totals.payableDays}</td></tr>
                <tr style={{ borderTop: '1px solid #e2e8f0', fontWeight: 600 }}>
                  <td style={{ padding: '4px 0' }}>Earned Base</td><td style={{ textAlign: 'right' }}>₹ {formatINR(totals.earnedBase)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {extras.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontWeight: 700, color: isMinimal ? '#111' : accent, marginBottom: 6 }}>Extra Tasks</div>
              <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                <tbody>
                  {extras.map((e) => (
                    <tr key={e.id}>
                      <td style={{ padding: '3px 0', color: '#666' }}>{e.date}</td>
                      <td>{e.description}</td>
                      <td style={{ textAlign: 'right' }}>₹ {formatINR(e.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
            <div>
              <div style={{ fontWeight: 700, color: isMinimal ? '#111' : accent, marginBottom: 6 }}>Allowances</div>
              <table style={{ width: '100%', fontSize: 12 }}>
                <tbody>
                  <tr><td>Bonus</td><td style={{ textAlign: 'right' }}>₹ {formatINR(allowances.bonus)}</td></tr>
                  <tr><td>Festival Bonus</td><td style={{ textAlign: 'right' }}>₹ {formatINR(allowances.festivalBonus)}</td></tr>
                  <tr><td>Travel</td><td style={{ textAlign: 'right' }}>₹ {formatINR(allowances.travelAllowance)}</td></tr>
                  <tr><td>Food</td><td style={{ textAlign: 'right' }}>₹ {formatINR(allowances.foodAllowance)}</td></tr>
                  <tr><td>Other</td><td style={{ textAlign: 'right' }}>₹ {formatINR(allowances.other)}</td></tr>
                  <tr style={{ fontWeight: 600, borderTop: '1px solid #e2e8f0' }}><td>Total</td><td style={{ textAlign: 'right' }}>₹ {formatINR(totals.allowanceTotal)}</td></tr>
                </tbody>
              </table>
            </div>
            <div>
              <div style={{ fontWeight: 700, color: '#b91c1c', marginBottom: 6 }}>Advances & Deductions</div>
              <table style={{ width: '100%', fontSize: 12 }}>
                <tbody>
                  {advances.map((a) => (
                    <tr key={a.id}><td style={{ color: '#666' }}>{a.date} · {a.reason}</td><td style={{ textAlign: 'right' }}>- ₹ {formatINR(a.amount)}</td></tr>
                  ))}
                  <tr><td>Breakage</td><td style={{ textAlign: 'right' }}>- ₹ {formatINR(deductions.breakage)}</td></tr>
                  <tr><td>Other</td><td style={{ textAlign: 'right' }}>- ₹ {formatINR(deductions.other)}</td></tr>
                  <tr style={{ fontWeight: 600, borderTop: '1px solid #fecaca' }}><td>Total Less</td><td style={{ textAlign: 'right' }}>- ₹ {formatINR(totals.advanceTotal + totals.deductionTotal)}</td></tr>
                </tbody>
              </table>
            </div>
          </div>

          <div style={{ background: accent, color: '#fff', padding: 14, borderRadius: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 11, opacity: 0.85 }}>NET PAYABLE</div>
              <div style={{ fontSize: 22, fontWeight: 800 }}>₹ {formatINR(finalAmount)}</div>
            </div>
            <div style={{ textAlign: 'right', fontSize: 12 }}>
              <div>Gross: ₹ {formatINR(totals.gross)}</div>
              <div>Mode: {meta.paymentMode}</div>
              <div>Status: {meta.paidStatus}</div>
            </div>
          </div>

          {notes && (
            <div style={{ marginTop: 12, fontSize: 12, color: '#555', fontStyle: 'italic' }}>{notes}</div>
          )}
        </div>
      </div>
    );
  };

  // ---------- RENDER ----------
  return (
    <div className="calculator-page fade-in">
      <SEO
        title="Daily Helper Bill Generator | Maid, Cook, Driver Salary Slip"
        description="Generate professional salary bills for house maids, cooks, drivers, gardeners, nannies and other daily helpers. Auto attendance, overtime, advances and 5 PDF templates."
        keywords="daily helper bill, maid salary slip, cook salary bill, driver salary, household help receipt, domestic worker bill"
        canonicalPath="/daily-helper-bill"
        calculatorType="DailyHelperBillGenerator"
        calculatorDescription="Generate domestic helper salary slips with attendance, overtime, advances, deductions and printable PDF templates."
      />

      <div className="calculator-header">
        <h1>Daily Helper Bill Generator</h1>
        <p>Create monthly salary bills for house maids, cooks, drivers, nannies and other daily helpers.</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          {/* Role picker */}
          <div className="input-group">
            <label>Helper Role</label>
            <div className="flex-row" style={{ flexWrap: 'wrap', gap: 8 }}>
              {HELPER_ROLES.map((r) => {
                const Icon = r.Icon;
                const active = role === r.v;
                return (
                  <button
                    key={r.v}
                    type="button"
                    onClick={() => switchRole(r.v)}
                    className={active ? 'btn btn-primary' : 'btn btn-outline'}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  >
                    <Icon /> {r.l}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Template picker */}
          <div className="input-group">
            <label>Template</label>
            <div className="flex-row" style={{ flexWrap: 'wrap', gap: 8 }}>
              {TEMPLATES.map((t) => {
                const active = template === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setTemplate(t.id)}
                    className={active ? 'btn btn-primary' : 'btn btn-outline'}
                    style={{ borderColor: active ? t.accent : undefined, background: active ? t.accent : undefined }}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          <LogoUploader logo={logo} setLogo={setLogo} />

          {/* Employer */}
          <h3>Employer / Household</h3>
          <div className="input-group"><label>Name</label><div className="input-with-icon"><input value={employer.name} onChange={(e) => updateEmployer('name', e.target.value)} /></div></div>
          <div className="input-group"><label>Address</label><div className="input-with-icon"><input value={employer.address} onChange={(e) => updateEmployer('address', e.target.value)} /></div></div>
          <div className="flex-row" style={{ gap: 8 }}>
            <div className="input-group" style={{ flex: 1 }}><label>Phone</label><div className="input-with-icon"><input value={employer.phone} onChange={(e) => updateEmployer('phone', e.target.value)} /></div></div>
            <div className="input-group" style={{ flex: 1 }}><label>Email</label><div className="input-with-icon"><input value={employer.email} onChange={(e) => updateEmployer('email', e.target.value)} /></div></div>
          </div>

          {/* Helper */}
          <h3>Helper Details</h3>
          <div className="flex-row" style={{ gap: 8 }}>
            <div className="input-group" style={{ flex: 1 }}><label>Name</label><div className="input-with-icon"><input value={helper.name} onChange={(e) => updateHelper('name', e.target.value)} /></div></div>
            <div className="input-group" style={{ flex: 1 }}><label>Role Title</label><div className="input-with-icon"><input value={helper.role} onChange={(e) => updateHelper('role', e.target.value)} /></div></div>
          </div>
          <div className="flex-row" style={{ gap: 8 }}>
            <div className="input-group" style={{ flex: 1 }}><label>Phone</label><div className="input-with-icon"><input value={helper.phone} onChange={(e) => updateHelper('phone', e.target.value)} /></div></div>
            <div className="input-group" style={{ flex: 1 }}><label>ID Proof</label><div className="input-with-icon"><input value={helper.idProof} onChange={(e) => updateHelper('idProof', e.target.value)} /></div></div>
          </div>
          <div className="input-group"><label>Address</label><div className="input-with-icon"><input value={helper.address} onChange={(e) => updateHelper('address', e.target.value)} /></div></div>
          <div className="flex-row" style={{ gap: 8 }}>
            <div className="input-group" style={{ flex: 1 }}><label>Joined On</label><div className="input-with-icon"><input value={helper.joinedOn} onChange={(e) => updateHelper('joinedOn', e.target.value)} /></div></div>
            <div className="input-group" style={{ flex: 1 }}><label>UPI ID</label><div className="input-with-icon"><input value={helper.upi} onChange={(e) => updateHelper('upi', e.target.value)} /></div></div>
          </div>
          <div className="flex-row" style={{ gap: 8 }}>
            <div className="input-group" style={{ flex: 1 }}><label>Bank A/C</label><div className="input-with-icon"><input value={helper.bankAccount} onChange={(e) => updateHelper('bankAccount', e.target.value)} /></div></div>
            <div className="input-group" style={{ flex: 1 }}><label>IFSC</label><div className="input-with-icon"><input value={helper.ifsc} onChange={(e) => updateHelper('ifsc', e.target.value)} /></div></div>
          </div>

          {/* Bill meta */}
          <h3>Bill Details</h3>
          <div className="flex-row" style={{ gap: 8 }}>
            <div className="input-group" style={{ flex: 1 }}><label>Bill No</label><div className="input-with-icon"><input value={meta.billNo} onChange={(e) => updateMeta('billNo', e.target.value)} /></div></div>
            <div className="input-group" style={{ flex: 1 }}><label>Pay Period</label><div className="input-with-icon"><input value={meta.period} onChange={(e) => updateMeta('period', e.target.value)} /></div></div>
          </div>
          <div className="flex-row" style={{ gap: 8 }}>
            <div className="input-group" style={{ flex: 1 }}><label>Issue Date</label><div className="input-with-icon"><input value={meta.issueDate} onChange={(e) => updateMeta('issueDate', e.target.value)} /></div></div>
            <div className="input-group" style={{ flex: 1 }}><label>Time</label><div className="input-with-icon"><input value={meta.time} onChange={(e) => updateMeta('time', e.target.value)} /></div></div>
          </div>
          <div className="flex-row" style={{ gap: 8 }}>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Payment Mode</label>
              <div className="input-with-icon">
                <select value={meta.paymentMode} onChange={(e) => updateMeta('paymentMode', e.target.value)}>
                  {PAYMENT_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Status</label>
              <div className="input-with-icon">
                <select value={meta.paidStatus} onChange={(e) => updateMeta('paidStatus', e.target.value)}>
                  {['Paid', 'Pending', 'Partial'].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Attendance */}
          <h3>Attendance & Salary</h3>
          <div className="flex-row" style={{ gap: 8 }}>
            <div className="input-group" style={{ flex: 1 }}><label>Monthly Salary (₹)</label><div className="input-with-icon"><input type="number" value={attendance.monthlySalary} onChange={(e) => updateAttendance('monthlySalary', e.target.value)} /></div></div>
            <div className="input-group" style={{ flex: 1 }}><label>Working Days</label><div className="input-with-icon"><input type="number" value={attendance.workingDays} onChange={(e) => updateAttendance('workingDays', e.target.value)} /></div></div>
          </div>
          <div className="flex-row" style={{ gap: 8 }}>
            <div className="input-group" style={{ flex: 1 }}><label>Days Present</label><div className="input-with-icon"><input type="number" value={attendance.daysPresent} onChange={(e) => updateAttendance('daysPresent', e.target.value)} /></div></div>
            <div className="input-group" style={{ flex: 1 }}><label>Paid Leaves</label><div className="input-with-icon"><input type="number" value={attendance.paidLeaves} onChange={(e) => updateAttendance('paidLeaves', e.target.value)} /></div></div>
            <div className="input-group" style={{ flex: 1 }}><label>Unpaid Leaves</label><div className="input-with-icon"><input type="number" value={attendance.unpaidLeaves} onChange={(e) => updateAttendance('unpaidLeaves', e.target.value)} /></div></div>
          </div>
          <div className="flex-row" style={{ gap: 8 }}>
            <div className="input-group" style={{ flex: 1 }}><label>Overtime Hours</label><div className="input-with-icon"><input type="number" value={attendance.overtimeHours} onChange={(e) => updateAttendance('overtimeHours', e.target.value)} /></div></div>
            <div className="input-group" style={{ flex: 1 }}><label>Overtime Rate (₹/hr)</label><div className="input-with-icon"><input type="number" value={attendance.overtimeRate} onChange={(e) => updateAttendance('overtimeRate', e.target.value)} /></div></div>
          </div>

          {/* Extras */}
          <h3>Extra Tasks / One-time Work</h3>
          {extras.map((e) => (
            <div key={e.id} className="flex-row" style={{ gap: 8, alignItems: 'flex-end' }}>
              <div className="input-group" style={{ flex: 1 }}><label>Date</label><div className="input-with-icon"><input value={e.date} onChange={(ev) => updateExtra(e.id, 'date', ev.target.value)} /></div></div>
              <div className="input-group" style={{ flex: 2 }}><label>Description</label><div className="input-with-icon"><input value={e.description} onChange={(ev) => updateExtra(e.id, 'description', ev.target.value)} /></div></div>
              <div className="input-group" style={{ flex: 1 }}><label>Amount (₹)</label><div className="input-with-icon"><input type="number" value={e.amount} onChange={(ev) => updateExtra(e.id, 'amount', ev.target.value)} /></div></div>
              <button type="button" className="btn btn-outline" onClick={() => removeExtra(e.id)} style={{ marginBottom: 16 }}><FaTrash /></button>
            </div>
          ))}
          <button type="button" className="btn btn-outline" onClick={addExtra}><FaPlus /> Add Extra Task</button>

          {/* Allowances */}
          <h3>Allowances & Bonus</h3>
          <div className="flex-row" style={{ gap: 8 }}>
            <div className="input-group" style={{ flex: 1 }}><label>Monthly Bonus</label><div className="input-with-icon"><input type="number" value={allowances.bonus} onChange={(e) => updateAllowances('bonus', e.target.value)} /></div></div>
            <div className="input-group" style={{ flex: 1 }}><label>Festival Bonus</label><div className="input-with-icon"><input type="number" value={allowances.festivalBonus} onChange={(e) => updateAllowances('festivalBonus', e.target.value)} /></div></div>
          </div>
          <div className="flex-row" style={{ gap: 8 }}>
            <div className="input-group" style={{ flex: 1 }}><label>Travel Allowance</label><div className="input-with-icon"><input type="number" value={allowances.travelAllowance} onChange={(e) => updateAllowances('travelAllowance', e.target.value)} /></div></div>
            <div className="input-group" style={{ flex: 1 }}><label>Food Allowance</label><div className="input-with-icon"><input type="number" value={allowances.foodAllowance} onChange={(e) => updateAllowances('foodAllowance', e.target.value)} /></div></div>
            <div className="input-group" style={{ flex: 1 }}><label>Other</label><div className="input-with-icon"><input type="number" value={allowances.other} onChange={(e) => updateAllowances('other', e.target.value)} /></div></div>
          </div>

          {/* Advances */}
          <h3>Advances Taken</h3>
          {advances.map((a) => (
            <div key={a.id} className="flex-row" style={{ gap: 8, alignItems: 'flex-end' }}>
              <div className="input-group" style={{ flex: 1 }}><label>Date</label><div className="input-with-icon"><input value={a.date} onChange={(ev) => updateAdvance(a.id, 'date', ev.target.value)} /></div></div>
              <div className="input-group" style={{ flex: 2 }}><label>Reason</label><div className="input-with-icon"><input value={a.reason} onChange={(ev) => updateAdvance(a.id, 'reason', ev.target.value)} /></div></div>
              <div className="input-group" style={{ flex: 1 }}><label>Amount (₹)</label><div className="input-with-icon"><input type="number" value={a.amount} onChange={(ev) => updateAdvance(a.id, 'amount', ev.target.value)} /></div></div>
              <button type="button" className="btn btn-outline" onClick={() => removeAdvance(a.id)} style={{ marginBottom: 16 }}><FaTrash /></button>
            </div>
          ))}
          <button type="button" className="btn btn-outline" onClick={addAdvance}><FaPlus /> Add Advance</button>

          {/* Deductions */}
          <h3>Other Deductions</h3>
          <div className="flex-row" style={{ gap: 8 }}>
            <div className="input-group" style={{ flex: 1 }}><label>Breakage / Damage</label><div className="input-with-icon"><input type="number" value={deductions.breakage} onChange={(e) => updateDeductions('breakage', e.target.value)} /></div></div>
            <div className="input-group" style={{ flex: 1 }}><label>Other Deduction</label><div className="input-with-icon"><input type="number" value={deductions.other} onChange={(e) => updateDeductions('other', e.target.value)} /></div></div>
          </div>
          <div className="input-group">
            <label>
              <input type="checkbox" checked={deductions.roundOff} onChange={(e) => updateDeductions('roundOff', e.target.checked)} />{' '}
              Round off net to nearest rupee
            </label>
          </div>

          <div className="input-group">
            <label>Notes / Remarks</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
          </div>

          <div className="button-group">
            <button type="button" className="btn btn-primary" onClick={handleDownloadPDF}><FaDownload /> Download PDF</button>
            <button type="button" className="btn btn-outline" onClick={handlePrint}><FaPrint /> Print</button>
            <button type="button" className="btn btn-outline" onClick={handleReset}><FaRedo /> Reset</button>
          </div>
        </div>

        <div className="calculator-results">
          <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {roleConfig && <roleConfig.Icon />} Bill Preview
          </h3>
          <Preview />
          <div style={{ marginTop: 12, textAlign: 'center', color: '#666', fontSize: 12 }}>
            Net Payable: ₹ {formatINR(finalAmount)} · Gross: ₹ {formatINR(totals.gross)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DailyHelperBillGenerator;
